import { memo, useCallback, useMemo } from "react";
import { useWatch, type Path, type UseFormReturn } from "react-hook-form";
import type { ClientResponseDTO } from "@/features/clients/types/client.types";
import type { ContractResponseDTO } from "@/features/contracts/types/contract.types";
import type { StockAvailabilityResponseDTO } from "@/features/stocks/types/stock.types";
import { RentalType } from "@/shared/types/enums";
import { type WizardStepDef } from "./WizardStepper";
import { WizardShell } from "./WizardShell";
import { useWizardNav } from "./useWizardNav";
import { RentalSummaryPanel } from "./RentalSummaryPanel";
import { StepType } from "./steps/StepType";
import { StepClientContract } from "./steps/StepClientContract";
import { StepPeriod } from "./steps/StepPeriod";
import { StepItems } from "./steps/StepItems";
import { StepPayments } from "./steps/StepPayments";
import { StepReview } from "./steps/StepReview";
import type { RentalCreateFormSchema } from "../rental-create-schema";

type FieldName = Path<RentalCreateFormSchema>;

interface RentalCreateWizardProps {
  form: UseFormReturn<RentalCreateFormSchema>;
  stockItems: StockAvailabilityResponseDTO[];
  sellerLabel: string | null;
  selectedClientLabel: string | null;
  selectedContractLabel: string | null;
  parentContractLabel: string | null;
  isSubmitting: boolean;
  onTypeChange: (type: RentalType.SUIT | RentalType.GOWN) => void;
  onSellerChange: (sellerId: number | null, sellerName: string | null) => void;
  onClientSelect: (client: ClientResponseDTO) => void;
  onClientCreateOpen: () => void;
  onClientClear: () => void;
  onContractSelect: (contract: ContractResponseDTO) => void;
  onContractCreateOpen: () => void;
  onContractClear: () => void;
  onParentContractSelect: (contract: ContractResponseDTO) => void;
  onParentContractClear: () => void;
  onStockAdd: (item: StockAvailabilityResponseDTO) => void;
  onStockRemove: (stockId: number) => void;
  onSubmit: () => void;
}

export const RentalCreateWizard = memo(function RentalCreateWizard({
  form,
  stockItems,
  sellerLabel,
  selectedClientLabel,
  selectedContractLabel,
  parentContractLabel,
  isSubmitting,
  onTypeChange,
  onSellerChange,
  onClientSelect,
  onClientCreateOpen,
  onClientClear,
  onContractSelect,
  onContractCreateOpen,
  onContractClear,
  onParentContractSelect,
  onParentContractClear,
  onStockAdd,
  onStockRemove,
  onSubmit,
}: RentalCreateWizardProps) {
  const rentalType = useWatch({ control: form.control, name: "rentalType" });
  const hasShoes = useWatch({ control: form.control, name: "hasShoes" });

  /**
   * A etapa de itens (3) é opcional — só valida `shoesPairQuantity` quando
   * SUIT + sapatos. As mensagens de "selecione/cadastre" do `superRefine`
   * recaem em `selectedClientId`/`selectedContractId`.
   */
  const getStepFields = useCallback(
    (index: number): FieldName[] => {
      switch (index) {
        case 0:
          return ["sellerId"];
        case 1:
          return ["selectedClientId", "selectedContractId"];
        case 2:
          return ["expectedOutAt", "expectedInAt"];
        case 3:
          return rentalType === RentalType.SUIT && hasShoes
            ? ["shoesPairQuantity"]
            : [];
        case 4:
          return ["totalValue"];
        default:
          return [];
      }
    },
    [rentalType, hasShoes],
  );

  const steps = useMemo<WizardStepDef[]>(
    () => [
      { key: "type", label: "Tipo" },
      { key: "client", label: "Cliente" },
      { key: "period", label: "Período" },
      {
        key: "items",
        label: rentalType === RentalType.SUIT ? "Peças" : "Itens",
      },
      { key: "payment", label: "Pagamento" },
      { key: "review", label: "Revisão" },
    ],
    [rentalType],
  );

  const { step, maxReached, goNext, goBack, goTo } = useWizardNav({
    form,
    steps,
    getStepFields,
  });

  return (
    <WizardShell
      form={form}
      steps={steps}
      step={step}
      maxReached={maxReached}
      isSubmitting={isSubmitting}
      onStepClick={goTo}
      onBack={goBack}
      onNext={goNext}
      onSubmit={onSubmit}
      summary={
        <RentalSummaryPanel
          form={form}
          sellerLabel={sellerLabel}
          clientLabel={selectedClientLabel}
          contractLabel={selectedContractLabel}
        />
      }
    >
      {step === 0 ? (
        <StepType
          form={form}
          onTypeChange={onTypeChange}
          onSellerChange={onSellerChange}
        />
      ) : null}
      {step === 1 ? (
        <StepClientContract
          form={form}
          selectedClientLabel={selectedClientLabel}
          selectedContractLabel={selectedContractLabel}
          parentContractLabel={parentContractLabel}
          onClientSelect={onClientSelect}
          onClientCreateOpen={onClientCreateOpen}
          onClientClear={onClientClear}
          onContractSelect={onContractSelect}
          onContractCreateOpen={onContractCreateOpen}
          onContractClear={onContractClear}
          onParentContractSelect={onParentContractSelect}
          onParentContractClear={onParentContractClear}
        />
      ) : null}
      {step === 2 ? <StepPeriod form={form} /> : null}
      {step === 3 ? (
        <StepItems
          form={form}
          stockItems={stockItems}
          onStockAdd={onStockAdd}
          onStockRemove={onStockRemove}
        />
      ) : null}
      {step === 4 ? <StepPayments form={form} /> : null}
      {step === 5 ? (
        <StepReview
          form={form}
          sellerLabel={sellerLabel}
          selectedClientLabel={selectedClientLabel}
          selectedContractLabel={selectedContractLabel}
          parentContractLabel={parentContractLabel}
          onEditStep={goTo}
        />
      ) : null}
    </WizardShell>
  );
});
